// FilterSheet — one sheet replaces a list page's whole toolbar at ≤880px
// (mobile overhaul Phase 0 §0.5.3; mockup §3.1 B). Pages hand it their
// existing filter state as sections; every change applies live to the list
// underneath, so the footer button can name the result ("Show 9 tasks") and
// "Apply" only closes. "Clear" sits in the header.

import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { Sheet, SheetButton, SheetSection } from './Sheet';
import { PickerSheet } from './PickerSheet';
import type { DropdownItem } from './primitives';

export type FilterSection =
  | {
      key: string;
      label: string;
      kind: 'chips';
      options: Array<{ value: string; label: string }>;
      /** A single value, or the chosen values when `multi`. */
      value: string | string[];
      multi?: boolean;
      onChange: (v: any) => void;
    }
  | {
      key: string;
      label: string;
      kind: 'picker';
      items: DropdownItem<string>[];
      value: string | null;
      /** Trigger text with nothing chosen — "All projects". */
      placeholder: string;
      onChange: (v: string | null) => void;
    }
  | {
      key: string;
      label: string;
      kind: 'toggle';
      value: boolean;
      onChange: (v: boolean) => void;
    };

export interface FilterSheetProps {
  open: boolean;
  onClose: () => void;
  sections: FilterSection[];
  /** Footer primary — the page computes it from its live filter state. */
  resultLabel: string;
  onApply: () => void;
  onClear: () => void;
}

export function FilterSheet({ open, onClose, sections, resultLabel, onApply, onClear }: FilterSheetProps) {
  // Which picker section has its PickerSheet stacked on top, if any.
  const [pickerKey, setPickerKey] = useState<string | null>(null);
  const picker = sections.find((s) => s.key === pickerKey && s.kind === 'picker');
  // A picker left stacked when the sheet was dismissed must not reappear
  // on the next open.
  useEffect(() => {
    if (!open) setPickerKey(null);
  }, [open]);

  return (
    <Sheet
      open={open}
      onClose={onClose}
      variant="half"
      title="Filter"
      rightAction={
        <SheetButton tone="accent" onClick={onClear}>
          Clear
        </SheetButton>
      }
      footer={
        <button type="button" className="btn primary" onClick={onApply}>
          {resultLabel}
        </button>
      }
      className="fsheet"
    >
      {sections.map((section) => (
        <Fragment key={section.key}>
          {section.kind !== 'toggle' && <SheetSection>{section.label}</SheetSection>}
          {section.kind === 'chips' && <ChipRow section={section} />}
          {section.kind === 'picker' && (
            <div className="fsheet-field">
              <button
                type="button"
                className={`fsheet-trigger${section.value === null ? ' empty' : ''}`}
                onClick={() => setPickerKey(section.key)}
              >
                <span>{labelFor(section.items, section.value) ?? section.placeholder}</span>
                <span className="chev" aria-hidden="true">
                  ▾
                </span>
              </button>
            </div>
          )}
          {section.kind === 'toggle' && (
            <button
              type="button"
              className="fsheet-toggle-row"
              aria-pressed={section.value}
              onClick={() => section.onChange(!section.value)}
            >
              <span>{section.label}</span>
              <span className={`toggle${section.value ? ' on' : ''}`} aria-hidden="true" />
            </button>
          )}
        </Fragment>
      ))}
      {picker !== undefined && picker.kind === 'picker' && (
        <PickerSheet
          open
          title={picker.label}
          items={picker.items}
          value={picker.value}
          onSelect={(v: string) => {
            picker.onChange(v);
            setPickerKey(null);
          }}
          onClose={() => setPickerKey(null)}
        />
      )}
    </Sheet>
  );
}

function labelFor(items: DropdownItem<string>[], value: string | null): ReactNode {
  if (value === null) return null;
  return items.find((i) => i.value === value)?.label ?? null;
}

/** A wrapping row of 38px chips. Single-select lights one; `multi` toggles.
    Whatever the page passes is literal — an "Any" option is just a chip with
    the page's own empty value. */
function ChipRow({ section }: { section: Extract<FilterSection, { kind: 'chips' }> }) {
  const chosen = Array.isArray(section.value) ? section.value : [section.value];
  return (
    <div className="chiprow">
      {section.options.map((o) => {
        const on = chosen.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            className={`c${on ? ' on' : ''}`}
            aria-pressed={on}
            onClick={() => {
              if (!section.multi) {
                section.onChange(o.value);
                return;
              }
              section.onChange(on ? chosen.filter((v) => v !== o.value) : [...chosen, o.value]);
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
